// Shared fan scoring module — canonical source of truth for scoring logic.
// Consumers: stream processing, API stats, and profile stats read models.

export type MetricConfig = {
  baseline: number;
  ceiling: number;
  weight: number;
};

export type LevelConfig = {
  minScore: number;
  level: number;
  name: string;
  vibe: string;
  shadowColor: string;
};

export type ScoringConfig = {
  baselineScore: number;
  ceilingScore: number;
  metrics: {
    streamTime: MetricConfig;
    moneySpent: MetricConfig;
    experiences: MetricConfig;
  };
  levels: LevelConfig[];
};

/**
 * Default scoring configuration — per-artist fan score.
 * Metric thresholds are per-artist values:
 * - streamTime: hours (stored as seconds, converted during calc)
 * - moneySpent: GBP (stored as cents, converted during calc)
 * - experiences: count (accesses count)
 */
export const DEFAULT_SCORING_CONFIG: ScoringConfig = {
  baselineScore: 50,
  ceilingScore: 95,
  metrics: {
    streamTime: { baseline: 15, ceiling: 150, weight: 0.40 },
    moneySpent: { baseline: 15, ceiling: 150, weight: 0.30 },
    experiences: { baseline: 1, ceiling: 5, weight: 0.30 },
  },
  levels: [
    { minScore: 0, level: 1, name: "Listener", vibe: "Just getting started", shadowColor: "#A78BFA" },
    { minScore: 15, level: 2, name: "Fan", vibe: "Regular engagement", shadowColor: "#3B82F6" },
    { minScore: 30, level: 3, name: "Supporter", vibe: "Above average commitment", shadowColor: "#2DD4BF" },
    { minScore: 45, level: 4, name: "Advocate", vibe: "Genuine dedication", shadowColor: "#4ADE80" },
    { minScore: 60, level: 5, name: "Champion", vibe: "Top 20% territory", shadowColor: "#FBBF24" },
    { minScore: 75, level: 6, name: "Superfan", vibe: "Elite, aspirational", shadowColor: "#FB923C" },
    { minScore: 90, level: 7, name: "Patron", vibe: "Legendary status, very rare", shadowColor: "#EF4444" },
  ],
};

/**
 * Calculate individual metric score using the normalization formula:
 * score = MIN(100, baseline_score * POWER(value/baseline, LOG(ceiling_score/baseline_score)/LOG(ceiling/baseline)))
 */
export function calculateMetricScore(
  value: number,
  metric: MetricConfig,
  baselineScore: number,
  ceilingScore: number,
): number {
  if (value <= 0) return 0;
  if (value >= metric.ceiling) return ceilingScore;

  if (metric.baseline <= 0 || metric.ceiling <= metric.baseline) {
    return baselineScore;
  }

  const exponent =
    Math.log(ceilingScore / baselineScore) /
    Math.log(metric.ceiling / metric.baseline);

  const score = baselineScore * Math.pow(value / metric.baseline, exponent);
  return Math.min(100, Math.max(0, score));
}

/**
 * Calculate per-artist fan score from artist-scoped metrics.
 * Uses moneySpentD2C for scoring (fiat purchases only).
 */
export function calculateArtistFanScore(
  stats: {
    streamTime: number; // seconds (per-artist)
    moneySpentD2C: number; // cents (D2C purchases, per-artist)
    accesses: number; // access count (per-artist)
  },
  config: ScoringConfig = DEFAULT_SCORING_CONFIG,
): number {
  const { baselineScore, ceilingScore, metrics } = config;

  const streamTimeHours = stats.streamTime / 3600;
  const moneySpentPounds = stats.moneySpentD2C / 100;

  const streamScore = calculateMetricScore(streamTimeHours, metrics.streamTime, baselineScore, ceilingScore);
  const spendScore = calculateMetricScore(moneySpentPounds, metrics.moneySpent, baselineScore, ceilingScore);
  const experienceScore = calculateMetricScore(stats.accesses, metrics.experiences, baselineScore, ceilingScore);

  const fanScore =
    metrics.streamTime.weight * streamScore +
    metrics.moneySpent.weight * spendScore +
    metrics.experiences.weight * experienceScore;

  return Math.round(fanScore * 100) / 100;
}

/**
 * Determine the user's level from their score.
 * Returns level info plus progress toward next level.
 */
export function determineLevelInfo(
  score: number,
  levels: LevelConfig[] = DEFAULT_SCORING_CONFIG.levels,
): {
  level: number;
  levelName: string;
  nextLevelName: string | null;
  levelProgress: number;
  shadowColor: string;
} {
  const sortedLevels = [...levels].sort((a, b) => b.minScore - a.minScore);
  const currentLevel = sortedLevels.find((l) => score >= l.minScore);

  if (!currentLevel) {
    const lowestLevel = levels[0];
    return {
      level: lowestLevel?.level ?? 1,
      levelName: lowestLevel?.name ?? "Listener",
      nextLevelName: levels[1]?.name ?? null,
      levelProgress: 0,
      shadowColor: lowestLevel?.shadowColor ?? "#FFD700",
    };
  }

  const ascendingLevels = [...levels].sort((a, b) => a.minScore - b.minScore);
  const currentIndex = ascendingLevels.findIndex((l) => l.level === currentLevel.level);
  const nextLevel =
    currentIndex < ascendingLevels.length - 1 ? ascendingLevels[currentIndex + 1] : null;

  let levelProgress = 0;
  if (nextLevel) {
    const levelRange = nextLevel.minScore - currentLevel.minScore;
    const progressInLevel = score - currentLevel.minScore;
    levelProgress = levelRange > 0 ? Math.min(1, Math.max(0, progressInLevel / levelRange)) : 1;
  } else {
    levelProgress = Math.min(1, score / 100);
  }

  return {
    level: currentLevel.level,
    levelName: currentLevel.name,
    nextLevelName: nextLevel?.name ?? null,
    levelProgress: Math.round(levelProgress * 100) / 100,
    shadowColor: currentLevel.shadowColor,
  };
}
